import { Wrench, ShieldCheck, Key, Scale, CheckCircle, Home, MessageSquare, FileCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const landlordServices = [
  { 
    title: "Guaranteed Rent", 
    desc: "Receive stable, monthly rental payments regardless of whether your property is occupied or vacant.",
    benefits: ["Fixed monthly income", "No void periods", "Guaranteed payments", "Financial stability"],
    icon: <ShieldCheck className="text-white" size={32} />
  },
  { 
    title: "Tenant Sourcing and Management", 
    desc: "We market your property, vet potential tenants rigorously, and manage them throughout the tenancy to ensure a smooth experience.",
    benefits: ["Thorough vetting", "Rigorous background checks", "Ongoing management", "Quality tenants"],
    icon: <Key className="text-white" size={32} />
  },
  { 
    title: "Maintenance and Repairs", 
    desc: "From routine upkeep to emergency repairs, we ensure your property is always in excellent condition, working with trusted contractors.",
    benefits: ["Routine upkeep", "Emergency support", "Trusted contractors", "Property preservation"],
    icon: <Wrench className="text-white" size={32} />
  },
  { 
    title: "Compliance and Legalities", 
    desc: "Let us take care of all landlord legal requirements, including safety certificates, tenancy agreements, and compliance with local regulations.",
    benefits: ["Safety certificates", "Legal agreements", "Regulatory compliance", "Risk protection"],
    icon: <Scale className="text-white" size={32} />
  }
];

const tenantServices = [
  { 
    title: "Premium Short-Term Rentals", 
    desc: "Browse our curated portfolio of high-quality properties available for short-term stays, vacation rentals, and serviced accommodation across the UK.",
    benefits: ["Fully furnished homes", "Flexible booking terms", "Professional cleaning", "Prime locations"],
    icon: <Home className="text-white" size={32} />
  },
  { 
    title: "Guest Support & Concierge", 
    desc: "Round-the-clock support throughout your stay with professional assistance for bookings, check-in/check-out, and any queries or emergencies.",
    benefits: ["24/7 availability", "Quick response times", "Professional service", "Hassle-free stays"],
    icon: <MessageSquare className="text-white" size={32} />
  },
  { 
    title: "Quality Assurance", 
    desc: "Every property meets our high standards for cleanliness, comfort, and amenities. Regular inspections ensure consistent excellence across our portfolio.",
    benefits: ["Regular inspections", "Quality furnishings", "Modern amenities", "Spotless properties"],
    icon: <FileCheck className="text-white" size={32} />
  },
  { 
    title: "Flexible Booking", 
    desc: "Easy online booking, transparent pricing, and flexible cancellation policies. Choose from weekend getaways to extended stays, all with complete peace of mind.",
    benefits: ["Easy online booking", "Clear pricing", "Flexible stays", "Secure payments"],
    icon: <Users className="text-white" size={32} />
  }
];

const Services = () => (
  <div className="w-full bg-white">
    <Helmet>
      <title>Property Management &amp; Short-Term Rental Services | ProjectProse</title>
      <meta
        name="description"
        content="Guaranteed rent, tenant sourcing, maintenance and legal compliance for landlords, plus fully furnished vacation and serviced accommodation for guests. Explore ProjectProse's full range of UK property services."
      />
      <link rel="canonical" href="https://www.project-prose.com/services" />
    </Helmet>
    <div className="pt-32 pb-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h2 className="text-sm uppercase tracking-[0.3em] text-prose-brand font-bold mb-4 border-b-4 w-fit border-prose-gold pb-2">Our Services</h2>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display text-prose-black">
            Premium Property <span className="italic font-light text-gray-400">Services.</span>
          </h1>
        </div>
        <p className="text-gray-600 text-sm sm:text-base md:text-lg mb-16 text-justify">
          At ProjectProse, we offer two distinct service lines: comprehensive property management for landlords and a premium portfolio of short-term vacation rentals and serviced accommodation. Whether you're a landlord seeking guaranteed rental income and professional management, or looking for quality vacation homes and flexible short-term stays, our dedicated team is here to provide expert solutions. We combine professional expertise with modern systems to ensure every property thrives.
        </p>
      </div>
    </div>

    <div className="px-6 pb-20">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-12">For Landlords</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {landlordServices.map((s, i) => (
            <div 
              key={i} 
              className={`group h-full rounded-2xl border p-8 transition-all duration-300 relative overflow-hidden hover:scale-105 hover:-translate-y-2 ${
                i % 2 === 1
                  ? 'border-prose-gold/30 hover:border-prose-gold hover:bg-prose-gold-light/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.25)]'
                  : 'border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-2xl'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />

              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300 ${
                i % 2 === 1 ? 'bg-prose-gold' : 'bg-prose-brand'
              }`}>
                {s.icon}
              </div>

              <h3 className="text-xl font-display font-bold mb-3 text-prose-black">
                {s.title}
              </h3>

              <p className="text-gray-600 text-sm leading-relaxed mb-6 text-justify">
                {s.desc}
              </p>

              <div className="space-y-2">
                {s.benefits.map((benefit, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <CheckCircle size={16} className={`flex-shrink-0 mt-0.5 ${i % 2 === 1 ? 'text-prose-gold' : 'text-prose-brand'}`} />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="px-6 pb-20 bg-gray-50">
      <div className="max-w-7xl mx-auto py-12 ">
        <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-12">For Guests - Vacation & Short-Term Stays</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tenantServices.map((s, i) => (
            <div 
              key={i} 
              className={`group h-full rounded-2xl border p-8 transition-all duration-300 relative overflow-hidden hover:scale-105 hover:-translate-y-2 ${
                i % 2 === 1
                  ? 'border-prose-gold/30 hover:border-prose-gold hover:bg-prose-gold-light/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.25)]'
                  : 'border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-2xl'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />

              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300 ${
                i % 2 === 1 ? 'bg-prose-gold' : 'bg-prose-brand'
              }`}>
                {s.icon}
              </div>

              <h3 className="text-xl font-display font-bold mb-3 text-prose-black">
                {s.title}
              </h3>

              <p className="text-gray-600 text-sm leading-relaxed mb-6 text-justify">
                {s.desc}
              </p>

              <div className="space-y-2">
                {s.benefits.map((benefit, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <CheckCircle size={16} className={`flex-shrink-0 mt-0.5 ${i % 2 === 1 ? 'text-prose-gold' : 'text-prose-brand'}`} />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="px-6 py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-4">Why Choose ProjectProse?</h2>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg">Expert property management that puts your interests first</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: "Industry Expertise",
              desc: "20+ years of combined experience in property management and investment optimization."
            },
            {
              title: "24/7 Support",
              desc: "Round-the-clock support for emergencies and urgent property matters, any day of the week."
            },
            {
              title: "Proven Track Record",
              desc: "Trusted by hundreds of property owners with consistent results and high satisfaction rates."
            },
            {
              title: "Transparent Pricing",
              desc: "No hidden fees. Clear, competitive pricing with detailed breakdowns of all costs."
            },
            {
              title: "Technology Driven",
              desc: "Modern tools and systems for efficient management, reporting, and account management."
            },
            {
              title: "Legal Compliance",
              desc: "Always up-to-date with regulations. Your property stays safe, legal, and protected."
            }
          ].map((item, i) => (
            <div key={i} className={`group p-6 rounded-xl border transition-all duration-300 hover:scale-105 hover:-translate-y-1 text-justify ${
              i % 2 === 1
                ? 'border-prose-gold/30 hover:border-prose-gold hover:bg-prose-gold-light/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.25)]'
                : 'border-gray-100 hover:border-prose-brand/30 hover:bg-white hover:shadow-xl'
            }`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                i % 2 === 1
                  ? 'bg-prose-gold/15 group-hover:bg-prose-gold/25'
                  : 'bg-prose-brand/10 group-hover:bg-prose-brand/20'
              }`}>
                <CheckCircle size={20} className={i % 2 === 1 ? 'text-prose-gold' : 'text-prose-brand'} />
              </div>
              <h3 className="font-bold text-prose-black mb-2 text-sm sm:text-base">{item.title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm text-justify">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="px-6 py-20 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-4">Gallery</h2>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">A glimpse into the properties we manage</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[250px] mb-12">
          {[
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-1.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-2 lg:row-span-2"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-2.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-3.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-4.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-5.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-6.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-2"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-7.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-8.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-9.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-1"
            },
            {
              image: "/images/siteImages/coventry-serviced-accommodation-room-10.jpg.jpeg",
              span: "sm:col-span-1 lg:col-span-2 lg:row-span-2"
            }
          ].map((item, i) => (
            <div
              key={i}
              className={`${item.span} group relative overflow-hidden rounded-lg cursor-pointer shadow-md hover:shadow-xl transition-all duration-300`}
            >
              <img
                src={item.image}
                alt={`Property ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300"></div>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="px-6 py-20">
      <div className="max-w-4xl mx-auto bg-prose-brand rounded-3xl p-8 sm:p-12 text-center text-white">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold mb-4">Ready to Transform Your Property Investment?</h2>
        <p className="text-white/90 mb-8 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">
          Let's discuss how ProjectProse can help you maximize returns and minimize stress.
        </p>
        <Link to="/contact" className="inline-block px-8 py-3 bg-white text-prose-brand rounded-lg font-semibold hover:bg-prose-gold hover:text-prose-black transition-all duration-300 text-sm sm:text-base">
          Schedule a Consultation
        </Link>
      </div>
    </div>
  </div>
);

export default Services;